/**
 * ClientOutdatedModal — what `useClientCompat` shows when this bundle is below
 * the live compatibility level (docs/spec/19d-client-backend-compat.md):
 *
 *   view 'modal' — „Itt az új Sendly", one button that runs the forced
 *                  update (busy label while it runs).
 *   view 'stuck' — the standing screen after the loop brake or an exhausted
 *                  recovery: „Az app nem tudott frissülni." plus, on a
 *                  supervised page, „Zárd be teljesen, és nyisd meg újra.";
 *                  on an unattended page „Újra próbálkozik…" (the hook keeps
 *                  retrying). Both levels and build ids, selectable, so they
 *                  can be pasted into a bug report.
 *
 * Neither can be dismissed: no backdrop click, no Escape (ModalBackdrop gets no
 * `onClose`), no close icon. The update is mandatory — the backend refuses this
 * build's callables already.
 *
 * Imported statically by App.jsx, not through lazyChunk: an outdated client is
 * exactly the one whose lazy chunks may no longer exist on Hosting.
 */
import React from "react";
import ReactDOM from "react-dom";
import { F } from "../constants";
import { ModalBackdrop, ModalScrollBody, GreenBtn, SELECTABLE } from "./ui";
import { useTranslation } from "../i18n";
import { useAppTheme } from "../contexts/ThemeContext";

// Above every app layer, the ViewportLayer banners and toasts (2000) included:
// nothing else on the page is usable while this is up.
export const CLIENT_OUTDATED_Z = 2100;

export function ClientOutdatedModal({ view, busy, unattended, ownLevel, ownBuildId, liveLevel, liveBuildId, onUpdate }) {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  if (view !== "modal" && view !== "stuck") return null;
  const stuck = view === "stuck";
  const title = stuck ? t("compat.stuck.title") : t("compat.modal.title");
  const body = stuck
    ? (unattended ? t("compat.stuck.retrying") : t("compat.stuck.body"))
    : t("compat.modal.body");

  const modal = (
    <ModalBackdrop closeOnBackdrop={false} closeOnEscape={false} zIndex={CLIENT_OUTDATED_Z} dim={0.6} ariaLabel={title}>
      <div style={{
        width: "100%", maxWidth: 420, background: th.card, borderRadius: 20,
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)", fontFamily: F, overflow: "hidden",
      }}>
        <ModalScrollBody padding={20} maxHeight="85vh" radius={20}>
          <div style={{ fontSize: 18, fontWeight: 800, color: th.text, marginBottom: 10 }}>
            {title}
          </div>
          <div style={{ fontSize: 14, color: th.text, lineHeight: 1.5, marginBottom: 16 }}>
            {body}
          </div>
          {stuck ? (
            <div style={{ ...SELECTABLE, fontSize: 12, color: th.textMuted, lineHeight: 1.5, fontFamily: "monospace" }}>
              <div>{t("compat.stuck.ownLevel", { level: ownLevel, build: ownBuildId || "—" })}</div>
              <div>{t("compat.stuck.liveLevel", { level: liveLevel ?? "—", build: liveBuildId || "—" })}</div>
            </div>
          ) : (
            <GreenBtn full onClick={onUpdate} disabled={!!busy}>
              {busy ? t("compat.modal.updating") : t("compat.modal.update")}
            </GreenBtn>
          )}
        </ModalScrollBody>
      </div>
    </ModalBackdrop>
  );
  // Portaled to <body> so no stacking context of the app tree can sit above it.
  return ReactDOM.createPortal(modal, document.body);
}
